/* Copyright (c) 2020-present CloudIO Inc */

import { SubscriptionEvent } from '@cloudio-saas/datasource-types';
import { atom, useStateXAtom, useStateXAtomValue } from '@cloudio-saas/statex';
import { Box } from '@mui/material';
import zIndex from 'constants/ZIndex';
import { clipboardIdAtom, draggingAtom } from 'designer/atoms';
import useDnD from 'designer/useDnD';
import { useIORef } from 'designer/useIORef';
import useSubscription from 'hooks/useSubscription';
import Icon from 'icons/Icon';
import React from 'react';
import useUser from 'root/useUser';
import EventClipboard from './EventClipboard';

const clipboardAtom = atom<SubscriptionEvent | null>({
  path: ['clipboard'],
  defaultValue: null,
});

export default function Clipboard({
  appUid,
  pageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement | null {
  const dragging = useStateXAtomValue(draggingAtom);
  const cref = useIORef<HTMLElement>();
  const mref = useIORef<HTMLElement>();
  const hoverId = useStateXAtomValue(clipboardIdAtom);
  const [event, setEvent] = useStateXAtom(clipboardAtom);
  const user = useUser();
  const pageUid = `clipboard-${user?.userName ?? '$x$'}`;
  useSubscription('cloudio', setEvent, 'Clipboard', pageUid);
  useDnD({
    appUid,
    pageId,
    item: {
      type: 'Clipboard',
      children: [],
      itemId: '$clipboard.copy$',
      props: {},
      parent: 'root',
    },
    ref: cref,
  });
  useDnD({
    appUid,
    pageId,
    item: {
      type: 'Clipboard',
      children: [],
      itemId: '$clipboard.move$',
      props: {},
      parent: 'root',
    },
    ref: mref,
  });

  if (!dragging && event) {
    return (
      <EventClipboard
        event={event}
        pageId={pageId}
        appUid={appUid}
        clearClipboard={() => {
          setEvent(null);
        }}
      />
    );
  }

  if (!dragging) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        boxShadow: 4,
        color: 'text.secondary',
        right: 8,
        p: 0,
        position: 'absolute',
        top: 8,
        zIndex: zIndex.DeveloperToolbar + 1,
        display: 'flex',
        width: 240,
        height: 60,
      }}
    >
      <Box
        ref={cref}
        sx={{
          width: 120,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          bgcolor: hoverId === '$clipboard.copy$' ? 'success.main' : undefined,
          color: hoverId === '$clipboard.copy$' ? '#fff' : undefined,
        }}
      >
        <Icon
          color={hoverId === '$clipboard.copy$' ? '#fff' : undefined}
          iconName="clipboard"
        />
        Copy
      </Box>
      <Box
        ref={mref}
        sx={{
          width: 120,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          bgcolor: hoverId === '$clipboard.move$' ? 'success.main' : undefined,
          color: hoverId === '$clipboard.move$' ? '#fff' : undefined,
        }}
      >
        <Icon
          color={hoverId === '$clipboard.move$' ? '#fff' : undefined}
          iconName="cut"
        />
        Cut
      </Box>
    </Box>
  );
}
